import { axiosRubyClient } from '@/util/axios/axiosClient';
import {
  Document,
  DocumentFormData,
  DocumentsIndexResponse,
  DocumentResponse,
  DocumentVersion,
  DocumentVersionsResponse,
  DocumentAuditsResponse,
  SignaturePositionData,
  UploadUrlResponse,
} from '@/types/documents';

export class DocumentsAPI {
  static async getDocuments(): Promise<DocumentsIndexResponse> {
    const response = await axiosRubyClient.get('/api/admin/documents');
    return response.data;
  }

  static async createDocument(
    data: DocumentFormData,
  ): Promise<DocumentResponse> {
    const response = await axiosRubyClient.post('/api/admin/documents', data);
    return response.data;
  }

  static async updateDocument(
    id: number,
    data: Partial<DocumentFormData & { active: boolean }>,
  ): Promise<DocumentResponse> {
    const response = await axiosRubyClient.patch(
      `/api/admin/documents/${id}`,
      data,
    );
    return response.data;
  }

  static async reorderDocuments(
    documentIds: number[],
  ): Promise<DocumentsIndexResponse> {
    const response = await axiosRubyClient.patch(
      '/api/admin/documents/reorder',
      { document_ids: documentIds },
    );
    return response.data;
  }

  static async getUploadUrl(
    filename: string,
    contentType?: string,
  ): Promise<UploadUrlResponse> {
    const params = new URLSearchParams();
    params.append('filename', filename);
    if (contentType) params.append('content_type', contentType);

    const response = await axiosRubyClient.get(
      `/api/admin/documents/upload_url?${params.toString()}`,
    );
    return response.data;
  }

  static async getVersions(
    documentId: number,
  ): Promise<DocumentVersionsResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/documents/${documentId}/versions`,
    );
    return response.data;
  }

  static async uploadVersion(
    documentId: number,
    s3Key: string,
  ): Promise<{ success: boolean; data: any }> {
    const response = await axiosRubyClient.post(
      `/api/admin/documents/${documentId}/versions`,
      { s3_key: s3Key },
    );
    return response.data;
  }

  static async updateVersion(
    documentId: number,
    versionId: number,
    data: SignaturePositionData,
  ): Promise<{ success: boolean; data: DocumentVersion }> {
    const response = await axiosRubyClient.patch(
      `/api/admin/documents/${documentId}/versions/${versionId}`,
      data,
    );
    return response.data;
  }

  static async testSign(
    documentId: number,
    signatureData: string,
  ): Promise<{
    success: boolean;
    data: { download_url: string; signed_document_id: number };
  }> {
    const response = await axiosRubyClient.post(
      `/api/admin/documents/${documentId}/test_sign`,
      { signature_data: signatureData },
    );
    return response.data;
  }

  static async getAudits(documentId: number): Promise<DocumentAuditsResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/documents/${documentId}/audits`,
    );
    return response.data;
  }
}
